import type { DashboardLayoutItem, WidgetSize } from "@/lib/api/dashboardLayout";

// The dashboard grid: a logical, ordered list of widgets turned into rows of
// column spans for the current screen width. The saved layout only stores
// order + size, never a pixel or column position, so a phone, a tablet and a
// desktop each get a layout that fits them without ever rewriting what the
// company saved.

export type GridColumns = 12 | 6 | 1;

/** Column span of each size preset on each grid. The desktop grid is 12
 * columns: four small cards, three medium, or a large + medium share a row. */
export const SIZE_SPANS: Record<GridColumns, Record<WidgetSize, number>> = {
  12: { small: 3, medium: 4, large: 8, full: 12 },
  6: { small: 3, medium: 3, large: 6, full: 6 },
  1: { small: 1, medium: 1, large: 1, full: 1 },
};

/** Breakpoints are on the width of the dashboard area itself, not the
 * browser window: the sidebar takes a slice of a laptop screen, so the
 * dashboard has to decide from the room it actually has. */
export const DESKTOP_MIN_WIDTH = 900;
export const TABLET_MIN_WIDTH = 560;

export function columnsForWidth(width: number): GridColumns {
  if (width >= DESKTOP_MIN_WIDTH) return 12;
  if (width >= TABLET_MIN_WIDTH) return 6;
  return 1;
}

/**
 * Column span of every widget, in order. Widgets fill rows left to right; when
 * the next one does not fit it starts a new row. A row that would end with an
 * empty gap hands the spare columns out to its widgets, so rows stay flush.
 * The one exception is a row of small KPI cards that would have to grow past
 * twice their preset (one lone KPI card must not turn into a full-width
 * banner): that row is left as it is. Medium, large and full widgets are
 * cards with room to grow, so they always fill their row.
 */
export function computeSpans(sizes: WidgetSize[], columns: GridColumns): number[] {
  const base = sizes.map((s) => SIZE_SPANS[columns][s]);
  if (columns === 1) return base;

  const spans = [...base];
  let rowStart = 0;
  let used = 0;
  const closeRow = (end: number) => {
    const count = end - rowStart;
    const spare = columns - used;
    if (count === 0 || spare <= 0) return;
    const each = Math.floor(spare / count);
    const remainder = spare - each * count;
    const grown = base.slice(rowStart, end).map((span, i) => span + each + (i >= count - remainder ? 1 : 0));
    const kpiTooWide = grown.some((span, i) => sizes[rowStart + i] === "small" && span > base[rowStart + i]! * 2);
    if (!kpiTooWide) {
      grown.forEach((span, i) => (spans[rowStart + i] = span));
    }
  };
  base.forEach((span, i) => {
    if (used + span > columns) {
      closeRow(i);
      rowStart = i;
      used = 0;
    }
    used += span;
  });
  closeRow(base.length);
  return spans;
}

// ------------------------------------------------------------ item editing

/** Items in logical order, with order renumbered 0..n-1. */
export function normalizeOrder(items: DashboardLayoutItem[]): DashboardLayoutItem[] {
  return [...items]
    .map((item, index) => ({ item, index }))
    .sort((a, b) => a.item.order - b.item.order || a.index - b.index)
    .map(({ item }, order) => ({ ...item, order }));
}

/** Visible widgets first (in the given order), hidden ones after, renumbered. */
function visibleFirst(items: DashboardLayoutItem[], visibleIds: string[]): DashboardLayoutItem[] {
  const byId = new Map(items.map((i) => [i.widget_id, i]));
  const shown = visibleIds.map((id) => byId.get(id)).filter((i): i is DashboardLayoutItem => Boolean(i));
  const hidden = normalizeOrder(items).filter((i) => !visibleIds.includes(i.widget_id));
  return [...shown, ...hidden].map((item, order) => ({ ...item, order }));
}

export function visibleIdsInOrder(items: DashboardLayoutItem[]): string[] {
  return normalizeOrder(items)
    .filter((i) => i.visible)
    .map((i) => i.widget_id);
}

/** Move a visible widget to another visible widget's slot (drag and drop). */
export function moveWidget(items: DashboardLayoutItem[], activeId: string, overId: string): DashboardLayoutItem[] {
  const ids = visibleIdsInOrder(items);
  const from = ids.indexOf(activeId);
  const to = ids.indexOf(overId);
  if (from < 0 || to < 0 || from === to) return items;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, activeId);
  return visibleFirst(items, next);
}

/** Move a visible widget one place earlier (-1) or later (+1) - the keyboard
 * and touch alternative to dragging. */
export function nudgeWidget(items: DashboardLayoutItem[], id: string, delta: -1 | 1): DashboardLayoutItem[] {
  const ids = visibleIdsInOrder(items);
  const from = ids.indexOf(id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= ids.length) return items;
  return moveWidget(items, id, ids[to]!);
}

/** Show or hide a widget. A widget being shown joins the end of the dashboard
 * rather than reappearing wherever it used to be. */
export function setWidgetVisible(items: DashboardLayoutItem[], id: string, visible: boolean): DashboardLayoutItem[] {
  const target = items.find((i) => i.widget_id === id);
  if (!target || target.visible === visible) return items;
  const toggled = items.map((i) => (i.widget_id === id ? { ...i, visible } : i));
  const ids = visibleIdsInOrder(items).filter((x) => x !== id);
  if (visible) ids.push(id);
  return visibleFirst(toggled, ids);
}

export function setWidgetSize(items: DashboardLayoutItem[], id: string, size: WidgetSize): DashboardLayoutItem[] {
  return items.map((i) => (i.widget_id === id ? { ...i, size } : i));
}

/** Whether two layouts are the same arrangement (order, visibility, size). */
export function sameLayout(a: DashboardLayoutItem[], b: DashboardLayoutItem[]): boolean {
  const na = normalizeOrder(a);
  const nb = normalizeOrder(b);
  return na.length === nb.length && na.every((x, i) => x.widget_id === nb[i]!.widget_id && x.visible === nb[i]!.visible && x.size === nb[i]!.size);
}
